import React, { ChangeEvent, useMemo, useState } from 'react'
import {
  FormErrorText,
  FormField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { NameDto } from 'api/Common/Types'
import { IBIndividualRebateData } from 'api/Trading-Accounts/Types/IntroducingBrokerRebates'
import { useAppSelector } from 'store'

interface Props {
  rebate: IBIndividualRebateData | null
  tradingAccount: NameDto<string> | null
  onSubmit: (rebateData: IBIndividualRebateData, tradingAccountId: string) => Promise<void>
}

interface RebateData {
  approvalType?: NameDto
  period?: NameDto
  rebateAmount?: string
  isDefault?: boolean
}

export const EditRebateModal: React.FC<Props> = (props) => {
  const { ibRebatesApprovalTypes, ibRebatesPeriods } = useAppSelector(
    ({ partnerships }) => partnerships
  )
  const { rebate, tradingAccount, onSubmit } = props
  const [data, setData] = useState<RebateData>({
    approvalType: ibRebatesApprovalTypes.find(({ id }) => id === rebate?.rebateApprovalTypeId),
    period: ibRebatesPeriods.find(({ id }) => id === rebate?.rebatePeriodId),
    rebateAmount: rebate?.rebateAmountInPercentage?.toString() || '',
    isDefault: rebate?.isDefault,
  })

  const isValid = useMemo(
    () =>
      data?.approvalType?.id &&
      data.period?.id !== undefined &&
      data.rebateAmount &&
      tradingAccount,
    [data, tradingAccount]
  )

  const handleSubmit = () => {
    if (!data?.approvalType?.id) return
    if (data?.period?.id === undefined) return
    if (!data?.rebateAmount) return
    if (!tradingAccount?.id) return

    const rebateAmount = parseFloat(data.rebateAmount)
    if (isNaN(rebateAmount)) return

    onSubmit(
      {
        rebateApprovalTypeId: data.approvalType.id,
        rebatePeriodId: data.period.id,
        rebateAmountInPercentage: rebateAmount,
        isDefault: data.isDefault ?? false,
      },
      tradingAccount.id
    )
  }

  const errors = useMemo(() => {
    const errors = { rebateAmount: '' }
    if (data?.rebateAmount && Number(data.rebateAmount) > 100) {
      errors.rebateAmount = 'Rebate amount must be less than 100'
    } else if (data?.rebateAmount && Number(data.rebateAmount) < 0.1) {
      errors.rebateAmount = 'Rebate amount must be greater than 0.1'
    }
    return errors
  }, [data])

  return (
    <DialogContent size='sm'>
      <DialogHeader>
        <DialogTitle>Edit Rebate</DialogTitle>
      </DialogHeader>

      <FormReadonlyField label='Trading Account' value={tradingAccount?.name || ''} />

      <FormSelectField
        label='Type'
        placeholder={ibRebatesApprovalTypes.map(({ name }) => name).join(' / ')}
        options={ibRebatesApprovalTypes}
        value={data?.approvalType?.id}
        showSearch
        onChange={(value) => {
          const approvalType = ibRebatesApprovalTypes.find(({ id }) => id === value)
          setData({ ...data, approvalType })
        }}
      />

      <FormSelectField
        label='Payout period'
        placeholder={ibRebatesPeriods.map(({ name }) => name).join(' / ')}
        options={ibRebatesPeriods}
        value={data?.period?.id}
        showSearch
        onChange={(value) => {
          const period = ibRebatesPeriods.find(({ id }) => id === value)
          setData({ ...data, period })
        }}
      />

      <FormField
        label='Rebate amount, %'
        type='number'
        value={data?.rebateAmount}
        min={0}
        max={100}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setData({ ...data, rebateAmount: e.target.value })
        }
      />
      <FormErrorText message={errors.rebateAmount} />

      <DialogFooter>
        <Button
          type='button'
          onClick={() => handleSubmit()}
          disabled={!isValid || Object.values(errors).some((x) => !!x)}
          data-test='edit-rebate-button'
        >
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
